// Record expense · capital in · drawing — chart of accounts §5 (expenses)
// and §3 (owner equity).
//
// This file owns the three NON-customer forms only. RecordMoney owns the
// "what kind of movement" selector and the page header and renders one of
// these bodies; the customer receipt/payment flow it already had is the daily
// path and stays exactly as it shipped.
//
// THE RULE THIS EXISTS TO ENFORCE (chart §3): an expense reduces profit, a
// drawing does not — a drawing is profit already earned, taken out. They post
// to different accounts and must never read as interchangeable on screen. The
// callout that said so under the header went on 14 Sept 2026 (SWEEP-SCOPE
// §5.6: its first line was the subtitle two lines above, nearly verbatim);
// the cross-reference now lives in "About this screen" (`about` below), which
// RecordMoney renders in its header.
//
// Rails come from the shared RailPicker: one pick fixes both the drawer and
// the asset, so an expense can never be booked against a rail that cannot
// hold the amount's currency. Nothing is pre-filled — the rail decides which
// drawer shrinks, so the operator picks it deliberately every time.

import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { errorWords, nothingLanded, readWords } from '../lib/errors';
import type { ReactNode } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, EXPENSE_CATEGORIES, type ExpenseCategory } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, makeReference } from '../lib/workflow';
import { amountOk, amountProblem } from '../lib/amount';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { AssetPicker } from '../components/AssetPicker';
import { Field, FieldRow } from '../components/Field';
import { BoxLines } from '../components/BoxLines';
import { ErrorLine } from '../components/ErrorLine';
import { AccountsDown, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { MoreDetails } from '../components/MoreDetails';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, railName, foldAsset, sameAsset } from '../lib/naming';
import { displayAsset, fmtAmount } from '../lib/format';
import { readState } from '../lib/read-state';

/** The three movements that are not a customer receipt or payment. */
export type RecordKind = 'expense' | 'capital' | 'drawing';

/** Header copy lives here so the page title, the About text and the submit
 *  label for a mode are written in one place. RecordMoney renders
 *  title/subtitle/help. `about` holds every sentence that used to sit in the
 *  form: the cross-reference to the mode it is most often confused with, and
 *  the two helper paragraphs that stood under "Paid by / Paid to" (§5.6). */
export const RECORD_KIND_COPY: Record<RecordKind, {
  title: string;
  subtitle: string;
  railLabel: string;
  submit: string;
  about: ReactNode;
}> = {
  expense: {
    title:     'Record expense',
    subtitle:  'A business cost — it reduces profit.',
    railLabel: 'Paid from',
    submit:    'Record expense',
    about: (
      <>
        <p>A business cost, booked under its category. Money you took for yourself is not a cost — record it as a drawing.</p>
        <p>
          <strong className="font-medium text-tg-text">Paid to</strong> (under More) is a note on the cost so you can
          find it later. It does not change what they owe or what you hold for them — to settle a balance, use Party.
        </p>
        <p>
          When <strong className="font-medium text-tg-text">a party paid it for us</strong>, the cost is booked under the
          category and goes on their balance as money you hold for them. No desk account moves.
        </p>
      </>
    ),
  },
  capital: {
    title:     'Record capital in',
    subtitle:  'Money you put into the desk — your stake, not income.',
    railLabel: 'Received into',
    submit:    'Record capital in',
    about: (
      <p>Money you put into the desk. It raises your stake — it is not income and never shows as profit. A bill the desk has to pay is an expense.</p>
    ),
  },
  drawing: {
    title:     'Record drawing',
    subtitle:  'Money you take out for yourself — not a cost.',
    railLabel: 'Taken from',
    submit:    'Record drawing',
    about: (
      <p>Money you take out for yourself. Not a cost — it is profit already earned, so profit is untouched. A bill the desk has to pay is an expense.</p>
    ),
  },
};

/** What the last posting said (the reference that landed, whether the BFF deduped it, and the facts the answer reads back). */
export interface Posted { reference: string; deduped: boolean; amount: number; asset: string; where: string; paidByParty: boolean }

/** The answer to a posting: a headline of what happened (design system §3: one line, at most ten words — the amount
 *  and its unit one unbreakable value) and, under it in muted type, where from or to. A deduped answer says so
 *  instead: the ledger already held this reference and nothing moved on this request. */
export function PostedLine({ posted, kind }: { posted: Posted; kind: RecordKind }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={posted.reference} />}>
        ✓ Recorded <span className="whitespace-nowrap">{fmtAmount(posted.amount, posted.asset)} {displayAsset(posted.asset)}</span>{' '}
        {kind === 'expense' ? 'expense' : kind === 'capital' ? 'capital in' : 'drawing'}
      </StateLine>
      <p className="text-meta text-tg-hint">
        {kind === 'expense' ? (posted.paidByParty ? `paid by ${posted.where}` : `from ${posted.where}`)
          : kind === 'capital' ? `into ${posted.where}`
          : `from ${posted.where}`}
      </p>
    </div>
  );
}

const REFERENCE_PREFIX: Record<RecordKind, string> = {
  expense: 'EXP',
  capital: 'CAP',
  drawing: 'DRAW',
};

export function RecordExpense({ kind }: { kind: RecordKind }) {
  // ?rail=&railAsset=&amount=&note=&category= — the same names Record money
  // reads — prefill a cost another screen already knows the figures of: the
  // Convert screen's "Record the fee" link after a venue conversion (15 Sept
  // 2026) carries the exchange's account, the fee and a note naming the
  // deal. Nothing posts until the operator taps; the picker still asks for
  // the account when the link named none.
  const [search] = useSearchParams();
  const [rail, setRail]         = useState<RailValue>(() => {
    const slug = search.get('rail'), asset = search.get('railAsset');
    return slug && asset ? { slug, asset } : { slug: '', asset: '' };
  });
  const [category, setCategory] = useState<ExpenseCategory>(() => {
    const c = search.get('category');
    return c && EXPENSE_CATEGORIES.some((x) => x.value === c) ? (c as ExpenseCategory) : 'other';
  });
  const [partySlug, setPartySlug] = useState('');
  const [amount, setAmount]     = useState(search.get('amount') ?? '');
  const [notes, setNotes]       = useState(search.get('note') ?? '');

  const copy = RECORD_KIND_COPY[kind];
  const isExpense = kind === 'expense';

  // Same query key as Move money / Treasury, so the rail list is already warm.
  const treasury = useQuery({
    queryKey: ['treasury', 'balances'],
    queryFn:  () => api.treasuryBalances(),
  });
  const rows = treasury.data?.rows ?? [];
  // What the account list is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED.
  const accounts = readState(treasury);

  // "Paid to" is an optional attribution on an expense (chart §5: commissions
  // and courier costs used to vanish into party balances). Same endpoint and
  // same option shape as every other party autocomplete. With balances, so
  // the "Paid by" party can read its box back in the standard words.
  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: partySlug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: isExpense && partySlug.length > 0,
  });
  const partyOptions: ComboboxOption[] = useMemo(() =>
    rankForQuery(parties.data?.parties ?? [], partySlug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: partySubtitle(p, { compact: true }),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, partySlug]);
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === partySlug),
    [parties.data, partySlug],
  );
  // WHAT THE LIST IS WORTH — `readState` (lib/read-state.ts; Appendix B): the names, and the balances (a list the BFF flags
  // `balances_read: false` has the names and not the balances: its empty balances are not "square").
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry — whatever is typed, a party picked or not (the list showing is the one read, and its names are on
  // screen while it is being searched: the failure must not wait for an exact pick).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  const amountNum   = Number(amount);
  // The same rule every amount box in the console uses, and now the same
  // sentence with it: a dead submit button never said what was wrong with the
  // number in front of it.
  const amountWords = amountProblem(amount);
  // WHO PAID. A cost the desk paid leaves a desk account; a cost a party paid
  // for the desk (JP Office settling an office bill, 11 Sept 2026) leaves
  // nothing of ours and goes on their balance as money we owe them.
  const [payer, setPayer] = useState<'desk' | 'party'>('desk');
  // The asset they paid in — the registry's codes, as the four-option select
  // this replaced sent them (AssetPicker since 14 Sept 2026, §5.6).
  const [partyAsset, setPartyAsset] = useState<string>('AED');
  const partyPaid   = isExpense && payer === 'party';
  const railOk      = partyPaid ? partySlug.trim() !== '' : (rail.slug !== '' && rail.asset !== '');
  // The party's box in the asset they paid in, before → after: a cost they
  // paid for the desk lowers what they owe (or raises what you hold). Read
  // from the list row's folded balances — the same figures every picker shows.
  const partyNow = (() => {
    const bag = selectedParty?.balances ?? {};
    return bag[foldAsset(partyAsset)] ?? bag[partyAsset] ?? 0;
  })();

  // ONE REFERENCE PER ENTRY. It was minted inside mutationFn on every submit,
  // so a retry after a timeout that had in fact posted made a second EXP-/CAP-/
  // DRW- entry instead of deduplicating (re-check, 5 Sept 2026). Held here,
  // cleared on success, and retired on a kind change only when nothing landed.
  const refRef = useRef<string | null>(null);
  // What the last posting said — the reference that landed, whether the BFF
  // deduped it, and the facts the success line reads back (never the live
  // fields, which clear on success).
  const [posted, setPosted] = useState<Posted | null>(null);
  const submit = useMutation({
    mutationFn: async () => {
      refRef.current ??= makeReference(REFERENCE_PREFIX[kind]);
      const reference = refRef.current;
      const body = {
        treasury_slug: rail.slug,
        amount:        { asset: rail.asset, amount: amountNum },
        reference,
        notes:         notes.trim() || undefined,
      };
      const railRow = rows.find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset));
      const facts = { reference, amount: amountNum, asset: rail.asset, where: railName(railRow ?? { slug: rail.slug }), paidByParty: partyPaid };
      if (kind === 'expense') {
        if (partyPaid) {
          const data = await api.recordExpense({
            amount:             { asset: partyAsset, amount: amountNum },
            reference,
            notes:              notes.trim() || undefined,
            category,
            paid_by_party_slug: partySlug.trim(),
          });
          return { data, ...facts, asset: partyAsset, where: selectedParty ? partyName(selectedParty) : partySlug.trim() };
        }
        const data = await api.recordExpense({
          ...body,
          category,
          party_slug: partySlug.trim() || undefined,
        });
        return { data, ...facts };
      }
      if (kind === 'capital') return { data: await api.recordCapital(body), ...facts };
      return { data: await api.recordDrawing(body), ...facts };
    },
    onSuccess: ({ data, ...facts }) => {
      refRef.current = null;
      setPosted({ ...facts, deduped: !!data.deduped });
      // The figures leave with the entry (§7 rule 12); the account, the
      // category and the payer stay for the next one.
      setAmount(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError:   () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  useEffect(() => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  }, [kind]);   // eslint-disable-line react-hooks/exhaustive-deps

  // A READ THAT FAILED IS NOT AN EMPTY ONE (the rail list and the party list both drew nothing and said nothing).
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;

  const notesField = (
    <Field label="Notes">
      <textarea
        className="input h-16"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={isExpense ? 'What it was for' : 'Anything to remember'}
      />
    </Field>
  );

  return (
    <div className="space-y-3 lg:space-y-4">
      {((accounts === 'failed' && !partyPaid) || (isExpense && lookup === 'failed')) && (
        <StateLine
          tone="warn"
          more={readWords(isExpense && lookup === 'failed' ? parties.error : treasury.error)}
          action={retry(() => { if (accounts === 'failed') void treasury.refetch(); if (lookup === 'failed') void parties.refetch(); })}
        >
          {`Couldn’t read ${isExpense && lookup === 'failed' && accounts === 'failed' && !partyPaid ? 'parties and accounts' : isExpense && lookup === 'failed' ? 'parties' : 'the accounts'} — try again.`}
        </StateLine>
      )}
      {partyPaid && listBalancesFlagged && (
        <StateLine tone="warn" action={retry(parties.refetch)}>
          Balances not read — try again.
        </StateLine>
      )}
      {isExpense && (
        <FieldRow>
          <Field label="Category">
            <select
              className="select w-full"
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </Field>

          {/* The one segmented toggle (styles.css .seg — the mode row above
              and Money in / out use it): the picked payer is lit. On the
              phone the two segments split the width at input height, like
              Book trade's side toggle; lg+ a content-sized bordered group.
              Its lg padding is one step tighter than Book trade's (p-0.5 /
              py-1, not p-1 / py-1.5) because this group alone sits beside a
              .select in a FieldRow: 1 + 2 + (1 + 4 + 19.5 + 4 + 1) + 2 + 1
              = 35.5px, the select's own height at lg, so the two boxes
              share a bottom edge instead of the group standing 8px proud. */}
          <Field label="Who paid">
            <div className="flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-0.5 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel">
              {([['desk', 'Desk account'], ['party', 'A party']] as const).map(([v, label]) => (
                <button key={v} type="button" onClick={() => setPayer(v)}
                  className={`seg whitespace-nowrap py-2 lg:py-1 ${payer === v ? 'seg-on' : ''}`}>{label}</button>
              ))}
            </div>
          </Field>
        </FieldRow>
      )}

      {partyPaid ? (
        <>
          {/* The party is the payer here, not an attribution: the posting goes
              on their balance (bff/src/routes/expenses.ts, paid_by_party_slug). */}
          <FieldRow>
            <Field label="Paid by" hint="goes on their balance">
              <Combobox
                value={partySlug}
                onChange={setPartySlug}
                options={partyOptions}
                more={rosterCut(parties.data)}
                placeholder="Search party"
                loading={partySlug.length > 0 && lookup === 'waiting'}
                emptyHint={partySlug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
              />
              {selectedParty && listBalancesRead && (
                // The box speaks the fold (21 Oct 2026 audit, as on Record money): a chain-coded
                // chip ("USDT·TRC") made the line read "USDT·TRC · owes you 250,000" for a box
                // that holds bare USDT. `partyNow` is read by the same fold.
                <BoxLines className="mt-1" asset={foldAsset(partyAsset)} before={partyNow} after={amountNum > 0 ? partyNow - amountNum : undefined} />
              )}
            </Field>
            <Field label="Asset">
              <AssetPicker value={partyAsset} onChange={setPartyAsset} options={ASSETS} />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label={`Amount in ${displayAsset(partyAsset)}`}>
              <input
                className="input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
              <ErrorLine>{amountWords}</ErrorLine>
            </Field>
          </FieldRow>
        </>
      ) : (
        <FieldRow>
          <Field label={copy.railLabel}>
            {/* No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party. */}
            {accounts === 'failed' && !treasury.data ? <AccountsDown value={rail} /> : (
              <RailPicker
                value={rail}
                onChange={setRail}
                rows={rows}
                loading={accounts === 'waiting'}
                readFailed={accounts === 'failed'}
                placeholder={kind === 'capital' ? 'Receiving account' : 'Paying account'}
              />
            )}
          </Field>

          <Field label={rail.asset ? `Amount in ${displayAsset(rail.asset)}` : 'Amount'}>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
              <div className="w-24 shrink-0 input bg-tg-bg/30 text-tg-hint text-center tabular text-body">
                {rail.asset ? displayAsset(rail.asset) : '—'}
              </div>
            </div>
            <ErrorLine>{amountWords}</ErrorLine>
          </Field>
        </FieldRow>
      )}

      {/* Notes stays OPEN on Expense (4 of 4 typed); on Capital and Drawing it
          folds under More with nothing else (0 entries either way). "Paid to"
          — an attribution only, the posting is rail → expense account — sits
          under More on a desk-paid expense (0 of 4 filled). §5.6. */}
      {isExpense && notesField}
      {isExpense && !partyPaid && (
        <MoreDetails label="More">
          <Field label="Paid to" hint="optional">
            <Combobox
              value={partySlug}
              onChange={setPartySlug}
              options={partyOptions}
              more={rosterCut(parties.data)}
              placeholder="Search party"
              loading={partySlug.length > 0 && lookup === 'waiting'}
              emptyHint={partySlug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
            />
          </Field>
        </MoreDetails>
      )}
      {!isExpense && (
        <MoreDetails label="More">
          {notesField}
        </MoreDetails>
      )}

      {/* lg+: hairline separator + right-aligned, content-sized submit —
          same shape as every other workflow form. */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={submit.isPending || !amountOk(amount) || !railOk}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Booking…' : copy.submit}
        </button>
      </div>

      {/* The success line names what posted and where, with the reference
          (§6). A deduped answer says so instead: the ledger already held this
          reference and nothing moved on this request. */}
      {submit.isSuccess && posted && <PostedLine posted={posted} kind={kind} />}
      {submit.isError && <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>}
    </div>
  );
}
